import { useState, Link, Button, Card, CardContent, CardHeader, CardTitle, Sparkles, CalendarDays, Share, Gift, HelpCircle, Mail, ChevronDown, Video, Link2 } from "@/pages/shared";
import { CLAIMS } from "@/config/claims";

export default function DashboardHelpPage() {
  const [openFaq, setOpenFaq] = useState(null);

  const quickTips = [
    { icon: Sparkles, title: "Generate Content", desc: "Content page → enter a topic → Generate. Set your Brand Kit in Settings for on-brand output", color: "text-violet-500", to: "/dashboard/content" },
    { icon: Share, title: "Connect Your Socials", desc: "Settings → connect your LinkedIn & X so scheduled posts publish to YOUR feeds", color: "text-blue-500", to: "/dashboard/settings" },
    { icon: CalendarDays, title: "Schedule & Auto-Post", desc: "Schedule any post and tick X and/or LinkedIn — Bluesky & Telegram cross-post automatically", color: "text-teal-500", to: "/dashboard/calendar" },
    { icon: Video, title: "AI Images & Video", desc: "View Full → Generate AI Image, or render a Sora 2 video clip (Pro+) for TikTok/Reels", color: "text-cyan-500", to: "/dashboard/videos" },
    { icon: Gift, title: "Earn Credits", desc: `Share your referral link to earn ${CLAIMS.referral.creditsPerSignup} credits per signup`, color: "text-amber-500", to: "/dashboard/referrals" },
    { icon: Link2, title: "Monetize", desc: "Affiliate links with auto-UTM tracking, lead capture forms, and PDF flyers", color: "text-purple-500", to: "/dashboard/affiliates" },
  ];

  const faqs = [
    { q: "How does auto-posting work?", a: "Click Schedule on any post, pick a time, and tick X and/or LinkedIn. If you've connected Bluesky or Telegram, they cross-post automatically too. Facebook & Instagram auto-publish to your own accounts is pending Meta review — use the one-click share buttons for those. No platform ticked = we email you a one-click-share reminder instead." },
    { q: "Why connect my own LinkedIn/X account?", a: "Without a connection, posts go via the Creatara account (X) or become email reminders (LinkedIn). Connect in Settings and posts publish as YOU, to YOUR followers. LinkedIn needs reconnecting every ~60 days — we'll email you first." },
    { q: "How many generations do I have?", a: `Free: ${CLAIMS.freeAllowance.generations}/${CLAIMS.freeAllowance.period} + referral bonuses | Pro: 100/month | Enterprise: Unlimited. ${CLAIMS.freeAllowance.resetRule}.` },
    { q: "Can I edit generated content?", a: "Yes! Click View Full → Edit, change anything, and Save. You can also 'Optimize for platform' to auto-fit X/LinkedIn/Instagram character limits." },
    { q: "What's the Complete Post Bundle?", a: "View Full → Build Complete Post: one click creates caption + AI image + hashtags + alt text + a first comment to pin. Copy the whole bundle at once." },
    { q: "How do video clips work?", a: "Pro+ plans: View Full → Video Clip section → pick TikTok/Reel or YouTube format → Generate. Sora 2 renders a 4s clip in 1–3 minutes. Scheduled X posts attach it automatically." },
    { q: "What does the 💰 score mean?", a: "AI-predicted revenue potential out of 100 — based on CTAs, urgency, benefits, length, and hashtags. Aim for 75+." },
    { q: "How do I track affiliate clicks?", a: "Go to Affiliates page. Each link shows real-time click counts, with UTM parameters added automatically." },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2">Help & Tips</h1>
        <p className="text-muted-foreground">Quick guides to get the most out of Creatara</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {quickTips.map((tip, i) => (
          <Link key={i} to={tip.to} data-testid={`quick-tip-${tip.to.split("/").pop()}`}>
            <Card className="glass card-hover h-full transition-colors hover:border-violet-500/40 cursor-pointer group">
              <CardContent className="p-4 flex items-start gap-4">
                <tip.icon className={`w-8 h-8 ${tip.color}`} />
                <div className="flex-1">
                  <h3 className="font-semibold flex items-center gap-2">{tip.title}<span className="text-violet-400 opacity-0 group-hover:opacity-100 transition-opacity">→</span></h3>
                  <p className="text-sm text-muted-foreground">{tip.desc}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <Card className="glass">
        <CardHeader><CardTitle>Frequently Asked Questions</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {faqs.map((faq, i) => (
            <div key={i} className="border-b border-white/10 last:border-0 pb-3 last:pb-0">
              <button
                className="w-full text-left py-2 flex justify-between items-center"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span className="font-medium">{faq.q}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === i && <p className="text-sm text-muted-foreground pb-2">{faq.a}</p>}
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex gap-4">
        <Link to="/help" className="flex-1">
          <Button variant="outline" className="w-full rounded-full"><HelpCircle className="w-4 h-4 mr-2" />Full Help Center</Button>
        </Link>
        <Link to="/contact" className="flex-1">
          <Button className="w-full btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full"><Mail className="w-4 h-4 mr-2" />Contact Support</Button>
        </Link>
      </div>
    </div>
  );
}
